import {
    pxArrayToRem,
    pxToRem,
    imageClassName,
    buttonClassName,
    designBoardPreviewTileSlotClassNames,
    boxClassName,
} from '@archipro-design/aria';
import type { StyleRule } from '@archipro-design/aria';
import { svgIconClassName } from '@archipro-design/icons';
import { BOARD_TILE_OVERLAY_CLASS } from '~/modules/design-board/config';

export const BaseGridStyle: StyleRule = ({ theme }) => ({
    margin: pxArrayToRem([42, 0, 60, 15]),
    gap: pxToRem(70),

    [theme.screen.tablet]: {
        margin: pxArrayToRem([48, 48, 70, 51]),
        gap: `${pxToRem(100)} ${pxToRem(20)}`,

        '& div': {
            cursor: 'pointer',
            [`& .${imageClassName}`]: {
                cursor: 'pointer',
            },
        },
    },

    [theme.screen.clamped]: {
        marginRight: 0,
    },
});

export const TileWrapper: StyleRule<{
    fileExtensions: {
        [x: string]: {
            content: string;
        };
    };
}> = ({ theme, fileExtensions }) => ({
    [`& .${BOARD_TILE_OVERLAY_CLASS}`]: {
        top: 'unset',
        right: pxToRem(20),
        padding: pxToRem(12),
        position: 'absolute',
        width: '100%',
        height: pxToRem(70),
        zIndex: theme.siteVariables.zIndexes.foreground,
        opacity: 1,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: pxToRem(12),

        [`& .${buttonClassName}`]: {
            height: pxToRem(36),
            width: pxToRem(36),
            borderRadius: '50%',
            background: theme.siteVariables.colors.white[300],
            '&:hover': {
                background: theme.siteVariables.colors.white[200],
            },
            [`& .${svgIconClassName}`]: {
                width: pxToRem(19),
                height: pxToRem(19),
            },
        },

        [theme.screen.tablet]: {
            top: pxToRem(2),
            right: 0,
            opacity: 0, // 0 means hide overlay by default on desktop
        },
    },

    [`& .${designBoardPreviewTileSlotClassNames.label}`]: {
        fontSize: pxToRem(14),
        lineHeight: 1.3,
        textTransform: 'uppercase',
        color: theme.siteVariables.colors.charcoal['000'],
        textOverflow: 'ellipsis',
        display: 'block',
        padding: pxArrayToRem([21, 8, 0, 8]),
    },
    [`& .${designBoardPreviewTileSlotClassNames.pinCount}`]: {
        fontSize: pxToRem(14),
        lineHeight: 1.3,
        color: theme.siteVariables.colors.grey[300],
        paddingLeft: pxToRem(8),
    },

    [`& .${designBoardPreviewTileSlotClassNames.imageGrid}`]: {
        '& div:first-child': {
            marginBottom: pxToRem(6),
        },
        [`& > .${boxClassName}`]: {
            border: `1px solid ${theme.siteVariables.colors.white[200]}`,

            [`& .${imageClassName}:before`]: {
                position: 'absolute',
                zIndex: theme.siteVariables.zIndexes.foreground,
                left: pxToRem(40),
                bottom: pxToRem(45),
                padding: pxArrayToRem([0, 2]),
                color: theme.siteVariables.colors.primitive.white,
                textTransform: 'uppercase',
                fontWeight: theme.siteVariables.fontWeightRegular,
                fontSize: pxToRem(6),
                background: theme.siteVariables.colors.charcoal[400],
                borderRadius: pxToRem(2.4),
            },

            [`&:nth-child(3)`]: {
                [`& .${imageClassName}:before`]: {
                    left: pxToRem(84),
                    bottom: pxToRem(93),
                    fontSize: pxToRem(12),
                    padding: pxArrayToRem([1, 6]),
                },
            },
            ...fileExtensions,
        },
    },

    [theme.screen.tablet]: {
        position: 'relative',
        [`& .${designBoardPreviewTileSlotClassNames.label}`]: {
            padding: pxArrayToRem([26, 0, 0, 0]),
        },
        [`& .${designBoardPreviewTileSlotClassNames.pinCount}`]: {
            paddingLeft: 0,
        },

        [`& .${designBoardPreviewTileSlotClassNames.imageGrid}`]: {
            [`& > .${boxClassName}`]: {
                [`& .${imageClassName}:before`]: {
                    left: pxToRem(39),
                    bottom: pxToRem(43),
                    padding: pxArrayToRem([0, 2]),

                    fontSize: pxToRem(6),
                },

                [`&:nth-child(3)`]: {
                    [`& .${imageClassName}:before`]: {
                        left: pxToRem(79),
                        bottom: pxToRem(90),
                        fontSize: pxToRem(12),
                        padding: pxArrayToRem([0, 6]),
                    },
                },
            },
        },

        '&:hover': {
            [`& .${BOARD_TILE_OVERLAY_CLASS}`]: {
                opacity: 1,
            },
            [`& .${designBoardPreviewTileSlotClassNames.label}`]: {
                textDecoration: 'underline',
            },
        },
    },
});
